import { Badge } from "@tql/ui-components-react/kendo/indicators";
import { Skeleton } from "@tql/ui-components-react/tql/indicators";
import { useRef } from "react";

import type { CarrierService } from "~/features/loads/components/load-details-drawer/flyout-details.ts";
import { useShipmentFlyout } from "~/features/loads/components/load-details-drawer/flyout-details.ts";
import { useShipmentDetails } from "~/features/loads/hooks/use-shipment-details.ts";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import type { Load } from "~/shared/schemas/loads-schema.ts";
import type { StopSection } from "~/shared/schemas/shipment-details-schema.ts";

import styles from "./drawer-shipment-details.module.css";
import { EM_DASH, emDashIfBlank } from "./em-dash-if-blank.ts";
import {
	formatContactNameAndPhone,
	formatLocalShortDate,
	formatServiceLevelWithDays,
	formatShortDate,
	getDisplayValue,
} from "./format-ltl-shipment-details.ts";
import { NonLtlShipmentDetailsCard } from "./non-ltl-shipment-details-card.tsx";

// Keep in sync with the `<=950px` panel-shrink rule in
// `../load-details-drawer.module.css`; when the drawer collapses to 360px, this
// component switches to its mobile layout.
const MOBILE_BREAKPOINT = 950;

type DrawerShipmentDetailsProps = {
	readonly load: Load | null;
};

const LOCATION_TYPE_MATCHERS: readonly {
	readonly match: string;
	readonly label:
		| "Residential"
		| "Commercial"
		| "Limited Access"
		| "Trade Show";
}[] = [
	{ match: "residential", label: "Residential" },
	{ match: "commercial", label: "Commercial" },
	{ match: "limited access", label: "Limited Access" },
	{ match: "trade show", label: "Trade Show" },
];

/**
 * Picks the first matching shed-type label from a stop's accessorial list.
 * Returns null when no shed-type accessorial is present so the Shed Type row
 * can be hidden entirely on the LTL layout.
 */
function getLocationTypeFromAccessorials(
	accessorials: string[],
): "Residential" | "Commercial" | "Limited Access" | "Trade Show" | null {
	for (const { match, label } of LOCATION_TYPE_MATCHERS) {
		if (accessorials.some((a) => a.toLowerCase() === match)) {
			return label;
		}
	}

	return null;
}

/**
 * Builds a single-row "City, ST ZIP" string. Empty / whitespace inputs are
 * dropped, and the comma is omitted when one side is missing so no orphan
 * commas appear (FR-008 / FR-016). Exported for re-use by sibling card
 * components and other drawer panels.
 */
export function getLocation(
	city: string | null | undefined,
	state: string | null | undefined,
	zip?: string | null,
) {
	const cityValue = city?.trim();
	const stateValue = state?.trim();
	const zipValue = zip?.trim();

	let location = "";

	if (cityValue && stateValue) {
		location = `${cityValue}, ${stateValue}`;
	} else if (cityValue) {
		location = cityValue;
	} else if (stateValue) {
		location = stateValue;
	}

	if (zipValue) {
		location = location ? `${location} ${zipValue}` : zipValue;
	}

	return location;
}

/**
 * Returns true when the supplied value is a parseable date-time string. Used
 * by the unified card's status badge state machine (FR-013): a malformed
 * timestamp is treated as not complete.
 */
export function isValidTimestamp(value: string | null | undefined): boolean {
	if (!value) {
		return false;
	}

	try {
		return !Number.isNaN(new Date(value).getTime());
	} catch {
		return false;
	}
}

/** Label rendered in the LTL pickup-date row when the pickup stop has not yet been loaded (FR-003 / US1). */
export const LTL_PICKUP_LABEL_ESTIMATED = "Estimated Pickup:";

/** Label rendered in the LTL pickup-date row once the pickup stop has been loaded (FR-004 / US1). */
export const LTL_PICKUP_LABEL_LOADED = "Picked Up On:";

/** Em-dash placeholder rendered in the LTL pickup-date value cell when `scheduledDate` is missing. Keeps the row mounted so the status-aware label remains visible (per PM/UX). */
const EMPTY_DATE_PLACEHOLDER = EM_DASH;

function renderValueSkeleton(width: number | string) {
	return (
		<span style={{ display: "inline-block", width }}>
			<Skeleton animation={{ type: "wave" }} shape={"text"} />
		</span>
	);
}

/** Renders one row of the right-hand stop detail columns. Hides itself when the value is empty and not loading. */
function DetailRow({
	label,
	value,
	isLoading,
	testId,
	multiline,
	skeletonWidth,
}: Readonly<{
	label: string;
	value: string;
	isLoading: boolean;
	testId: string;
	multiline?: boolean;
	skeletonWidth?: number | string;
}>) {
	const valueClassName = multiline
		? `${styles.detailValue} ${styles.detailValueMultiline}`
		: styles.detailValue;

	return (
		<div className={styles.detailRow}>
			<span className={styles.detailLabel}>{label}</span>
			<span className={valueClassName} data-testid={testId}>
				{isLoading ? renderValueSkeleton(skeletonWidth ?? 140) : value}
			</span>
		</div>
	);
}

function LtlAddressBlock({
	stop,
	type,
	isLoading,
}: Readonly<{
	stop: StopSection | null;
	type: "pickup" | "delivery";
	isLoading: boolean;
}>) {
	const facilityName = stop?.facilityName?.trim();
	const address1 = stop?.address1?.trim();
	const address2 = stop?.address2?.trim();
	const streetAddress =
		address1 && address2 ? `${address1}, ${address2}` : (address1 ?? address2);
	const cityStateZip = getLocation(stop?.city, stop?.state, stop?.postalCode);
	const country = stop?.country?.trim();
	const cityStateZipCountry =
		cityStateZip && country
			? `${cityStateZip}, ${country}`
			: (cityStateZip ?? country);

	if (isLoading) {
		return (
			<div className={styles.addressBlock}>
				<p className={styles.addressTitle}>Address</p>
				<p className={styles.addressFacility}>{renderValueSkeleton(180)}</p>
				<p className={styles.addressLine}>{renderValueSkeleton(160)}</p>
				<p className={styles.addressLine}>{renderValueSkeleton(140)}</p>
			</div>
		);
	}

	const hasAnyAddressLine = Boolean(
		facilityName || streetAddress || cityStateZipCountry,
	);

	if (!hasAnyAddressLine) {
		return (
			<div className={styles.addressBlock}>
				<p className={styles.addressTitle}>Address</p>
				<p
					className={styles.addressLine}
					data-testid={`ltl-${type}-address-empty`}
				>
					{EM_DASH}
				</p>
			</div>
		);
	}

	return (
		<div className={styles.addressBlock}>
			<p className={styles.addressTitle}>Address</p>
			{facilityName ? (
				<p
					className={styles.addressFacility}
					data-testid={`ltl-${type}-facility-name`}
				>
					{facilityName}
				</p>
			) : null}
			{streetAddress ? (
				<p className={styles.addressLine}>{streetAddress}</p>
			) : null}
			{cityStateZipCountry ? (
				<p className={styles.addressLine}>{cityStateZipCountry}</p>
			) : null}
		</div>
	);
}

/**
 * Picks the horizontal-gap utility class for the LTL stop section header so
 * the status badge sits the same visual distance from the title regardless
 * of which of the four badge labels is rendered. Mirrors the per-label gap
 * tuning used by `non-ltl-shipment-details-card.tsx` so LTL and non-LTL
 * cards stay visually consistent.
 */
function getLtlHeaderGapClassName(
	badgeText: "Loaded" | "Not Loaded" | "Delivered" | "Not Delivered",
): string {
	switch (badgeText) {
		case "Loaded": {
			return "k-d-flex k-gap-10";
		}
		case "Delivered": {
			return "k-d-flex k-gap-12";
		}
		case "Not Delivered": {
			return "k-d-flex k-gap-16";
		}
		default: {
			return "k-d-flex k-gap-15";
		}
	}
}

function LtlStopDateRow({
	stop,
	type,
	isComplete,
	isLoading,
}: Readonly<{
	stop: StopSection | null;
	type: "pickup" | "delivery";
	isComplete: boolean;
	isLoading: boolean;
}>) {
	if (type === "pickup") {
		// Pickup On reflects the actual loaded timestamp once available so the
		// displayed date matches the user's local timezone. Until the stop is
		// loaded we fall back to the scheduled date (also local-tz) so the row
		// still surfaces a useful estimate.
		const pickupValue = isComplete
			? (emDashIfBlank(formatLocalShortDate(stop?.actualTimestamp)) ??
				EMPTY_DATE_PLACEHOLDER)
			: (formatLocalShortDate(stop?.scheduledDate) ?? EMPTY_DATE_PLACEHOLDER);
		const pickupLabel = isComplete
			? LTL_PICKUP_LABEL_LOADED
			: LTL_PICKUP_LABEL_ESTIMATED;
		return (
			<DetailRow
				isLoading={isLoading}
				label={pickupLabel}
				skeletonWidth={120}
				testId={"ltl-pickup-date-row"}
				value={pickupValue}
			/>
		);
	}

	if (!isComplete) {
		return (
			<DetailRow
				isLoading={isLoading}
				label={"Estimated Delivery:"}
				skeletonWidth={120}
				testId={"ltl-delivery-date-row"}
				value={
					formatLocalShortDate(stop?.scheduledDate) ?? EMPTY_DATE_PLACEHOLDER
				}
			/>
		);
	}

	return (
		<DetailRow
			isLoading={isLoading}
			label={"Delivered On:"}
			skeletonWidth={120}
			testId={"ltl-delivery-date-row"}
			value={emDashIfBlank(formatLocalShortDate(stop?.actualTimestamp))}
		/>
	);
}

function LtlStopSection({
	stop,
	type,
	isLoading,
}: Readonly<{
	stop: StopSection | null;
	type: "pickup" | "delivery";
	isLoading: boolean;
}>) {
	const isMobile = useIsMobile(MOBILE_BREAKPOINT);
	const label = type === "pickup" ? "Pickup" : "Delivery";
	const isComplete = isValidTimestamp(stop?.actualTimestamp);

	let badgeText: "Loaded" | "Not Loaded" | "Delivered" | "Not Delivered";
	if (type === "pickup") {
		badgeText = isComplete ? "Loaded" : "Not Loaded";
	} else {
		badgeText = isComplete ? "Delivered" : "Not Delivered";
	}

	const referenceText = emDashIfBlank(stop?.referenceNumber);
	const shipmentTypeText = emDashIfBlank(
		getLocationTypeFromAccessorials(stop?.accessorials ?? []),
	);
	const hoursText = formatShipmentSpecificHours(
		stop?.timeType,
		stop?.appointmentStartTime,
		stop?.appointmentEndTime,
	);
	const contactText = emDashIfBlank(
		formatContactNameAndPhone(
			stop?.contactName,
			stop?.contactPhone,
			stop?.phoneExtension,
			!isMobile,
		),
	);

	return (
		<section
			className={styles.stopSection}
			data-testid={`ltl-shipment-${type}-section`}
		>
			<div
				className={getLtlHeaderGapClassName(badgeText)}
				style={{ alignItems: "center" }}
			>
				<h4 style={{ margin: 0, fontWeight: "normal" }}>{label}</h4>
				<span data-testid={`ltl-${type}-status-badge`}>
					<Badge
						align={{ horizontal: "start", vertical: "bottom" }}
						className={styles.stopBadge}
						fillMode={"outline"}
						rounded={"full"}
						size={"medium"}
						style={{
							color: isComplete
								? undefined
								: "var(--kendo-color-on-app-surface)",
						}}
						themeColor={isComplete ? "success" : "error"}
					>
						{badgeText}
					</Badge>
				</span>
			</div>

			<div className={styles.stopGrid}>
				<div className={styles.stopGridAddress}>
					<LtlAddressBlock isLoading={isLoading} stop={stop} type={type} />
				</div>
				<div className={`${styles.detailColumn} ${styles.detailColumnMiddle}`}>
					<LtlStopDateRow
						isComplete={isComplete}
						isLoading={isLoading}
						stop={stop}
						type={type}
					/>
					<DetailRow
						isLoading={isLoading}
						label={type === "pickup" ? "Pickup Number:" : "Delivery Number:"}
						skeletonWidth={120}
						testId={`ltl-${type}-reference`}
						value={referenceText}
					/>
					<DetailRow
						isLoading={isLoading}
						label={"Shipment Type:"}
						skeletonWidth={120}
						testId={`ltl-${type}-shipment-type`}
						value={shipmentTypeText}
					/>
				</div>
				<div className={`${styles.detailColumn} ${styles.detailColumnRight}`}>
					<DetailRow
						isLoading={isLoading}
						label={type === "pickup" ? "Shipping Hours:" : "Delivery Hours:"}
						skeletonWidth={140}
						testId={`ltl-${type}-hours`}
						value={hoursText}
					/>
					<DetailRow
						multiline
						isLoading={isLoading}
						label={"Contact:"}
						skeletonWidth={160}
						testId={`ltl-${type}-contact`}
						value={contactText}
					/>
				</div>
			</div>
		</section>
	);
}

const shipmentHoursFormatter = new Intl.DateTimeFormat("en-US", {
	hour: "numeric",
	minute: "2-digit",
	hour12: true,
});

function formatShipmentSpecificHours(
	timeType: StopSection["timeType"] | null | undefined,
	startTime: string | null | undefined,
	endTime: string | null | undefined,
): string {
	const prefix = getShipmentHoursPrefix(timeType);

	if (prefix === "TBD") {
		return prefix;
	}

	const startDisplay = formatShipmentHour(startTime);

	if (startDisplay === null) {
		return prefix;
	}

	const endDisplay = formatShipmentHour(endTime);
	if (
		endDisplay === null ||
		(startDisplay === endDisplay && timeType === "Appointment")
	) {
		return `${prefix} ${startDisplay}`;
	}

	return `${prefix} ${startDisplay} - ${endDisplay}`;
}

function getShipmentHoursPrefix(
	timeType: StopSection["timeType"] | null | undefined,
): string {
	if (timeType == null) {
		return "TBD";
	}

	switch (timeType) {
		case "Unknown": {
			return "TBD";
		}
		case "Fcfs": {
			return "FCFS";
		}
		case "Appointment": {
			return "Appointment";
		}
		default: {
			return String(timeType);
		}
	}
}

function formatShipmentHour(value: string | null | undefined): string | null {
	if (value == null) {
		return null;
	}

	const trimmed = value.trim();
	if (trimmed === "") {
		return null;
	}

	const parsed = new Date(trimmed);
	if (Number.isNaN(parsed.getTime())) {
		return null;
	}

	return shipmentHoursFormatter.format(parsed);
}

/** Rate types that retain the legacy single-stop LTL Shipment Details layout. */
const LTL_RATE_TYPES = new Set(["LTL", "PARTIALS DEPT LTL"]);

function isLtlRateType(rateType: string | null | undefined): boolean {
	return rateType != null && LTL_RATE_TYPES.has(rateType);
}

/** FR-010 skeleton widths for the LTL carrier summary row cells. */
const LTL_SUMMARY_SKELETON_WIDTHS = {
	carrier: 120,
	serviceLevel: 160,
	pro: 100,
	agentPro: 100,
	expiration: 90,
} as const;

type SummaryCellProps = {
	readonly label: string;
	readonly value: string | null;
	readonly isLoading: boolean;
	readonly skeletonWidth: number;
	readonly testId: string;
	readonly hideWhenEmpty?: boolean;
};

function SummaryCell({
	label,
	value,
	isLoading,
	skeletonWidth,
	testId,
	hideWhenEmpty = false,
}: Readonly<SummaryCellProps>) {
	if (!isLoading && value === null && hideWhenEmpty) {
		return null;
	}

	const displayValue = value ?? EM_DASH;

	return (
		<div className={styles.ltlSummaryCell} data-testid={testId}>
			<p className={styles.blockLabel}>{label}</p>
			<p className={styles.ltlSummaryValue}>
				{isLoading ? (
					<span style={{ display: "inline-block", width: skeletonWidth }}>
						<Skeleton animation={{ type: "wave" }} shape={"text"} />
					</span>
				) : (
					displayValue
				)}
			</p>
		</div>
	);
}

type LtlCarrierSummaryRowProps = {
	readonly carrier: CarrierService | undefined;
	readonly isLoading: boolean;
};

/**
 * Renders the LTL carrier summary row (Carrier, Service Level / Days, PRO #,
 * Agent PRO, Expiration Date) as the first child of the LTL Shipment Details
 * section. Each cell is independently hidden post-resolve when its value is
 * missing per FR-007. While loading, all five cells render skeletons.
 */
function LtlCarrierSummaryRow({
	carrier,
	isLoading,
}: Readonly<LtlCarrierSummaryRowProps>) {
	const carrierValue = getDisplayValue(carrier?.carrierName);
	const serviceLevelValue = formatServiceLevelWithDays(
		carrier?.serviceLevel,
		carrier?.serviceLevelAbbreviation,
		carrier?.transitDays,
	);
	const proValue = getDisplayValue(carrier?.proNumber);
	const agentProValue = getDisplayValue(carrier?.agentProNumber);
	const expirationValue = formatShortDate(carrier?.carrierQuoteExpirationDate);

	// While loading, render all 5 cells (skeletons). Post-resolve, Carrier,
	// Service Level / Days, PRO #, and Expiration Date keep their labels and
	// render an em dash when their value is null. Agent PRO retains the
	// hide-when-null behavior (per PM). If every cell would be hidden or em-
	// dashed (i.e. no carrier data at all), omit the row so no leftover divider
	// sits above the pickup section.
	if (
		!isLoading &&
		carrierValue === null &&
		serviceLevelValue === null &&
		proValue === null &&
		agentProValue === null &&
		expirationValue === null
	) {
		return null;
	}

	return (
		<div className={styles.ltlSummaryRow} data-testid={"ltl-summary-row"}>
			<SummaryCell
				isLoading={isLoading}
				label={"Carrier"}
				skeletonWidth={LTL_SUMMARY_SKELETON_WIDTHS.carrier}
				testId={"ltl-summary-carrier"}
				value={carrierValue}
			/>
			<SummaryCell
				isLoading={isLoading}
				label={"Service Level / Days"}
				skeletonWidth={LTL_SUMMARY_SKELETON_WIDTHS.serviceLevel}
				testId={"ltl-summary-service-level"}
				value={serviceLevelValue}
			/>
			<SummaryCell
				isLoading={isLoading}
				label={"PRO #"}
				skeletonWidth={LTL_SUMMARY_SKELETON_WIDTHS.pro}
				testId={"ltl-summary-pro"}
				value={proValue}
			/>
			<SummaryCell
				hideWhenEmpty
				isLoading={isLoading}
				label={"Agent PRO"}
				skeletonWidth={LTL_SUMMARY_SKELETON_WIDTHS.agentPro}
				testId={"ltl-summary-agent-pro"}
				value={agentProValue}
			/>
			<SummaryCell
				isLoading={isLoading}
				label={"Expiration Date"}
				skeletonWidth={LTL_SUMMARY_SKELETON_WIDTHS.expiration}
				testId={"ltl-summary-expiration"}
				value={expirationValue}
			/>
		</div>
	);
}

/** FR-018: skeleton placeholder for a single section in the unified non-LTL card. */
function renderNonLtlCardSectionSkeleton(key: string) {
	return (
		<section key={key} className={styles.cardSection}>
			<div className={styles.cardSectionHeader}>
				<h4 className={styles.cardSectionTitle}>
					<Skeleton animation={{ type: "wave" }} shape={"text"} />
				</h4>
			</div>
			<div
				className={styles.cardGrid}
				style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}
			>
				{[0, 1, 2].map((columnIndex) => (
					<div key={columnIndex} className={styles.cardColumn}>
						<p className={styles.cardColumnLabel}>
							<Skeleton animation={{ type: "wave" }} shape={"text"} />
						</p>
						<p className={styles.cardColumnValue}>
							<Skeleton animation={{ type: "wave" }} shape={"text"} />
						</p>
					</div>
				))}
			</div>
		</section>
	);
}

const DEFAULT_NON_LTL_SKELETON_STOP_COUNT = 2;

export function DrawerShipmentDetails({
	load,
}: Readonly<DrawerShipmentDetailsProps>) {
	const isMobile = useIsMobile(MOBILE_BREAKPOINT);
	const mobileClass = isMobile ? ` ${styles.mobile}` : "";

	const { data, isLoading, isError, isNotFound } = useShipmentDetails(
		load?.poNumber,
	);

	const isLtl = isLtlRateType(load?.rateType);

	const flyoutQuery = useShipmentFlyout(load?.poNumber, isLtl);
	const carrier = flyoutQuery.data?.carrierService;
	const isCarrierLoading = isLtl && flyoutQuery.isLoading;

	const lastNonLtlStopCountRef = useRef<number | null>(null);

	if (isLoading) {
		if (isLtl) {
			return (
				<div
					className={`${styles.sectionList}${mobileClass}`}
					data-testid={"shipment-details-loading"}
				>
					<LtlCarrierSummaryRow
						carrier={carrier}
						isLoading={isCarrierLoading}
					/>
					<LtlStopSection isLoading stop={null} type={"pickup"} />
					<LtlStopSection isLoading stop={null} type={"delivery"} />
				</div>
			);
		}

		const stopCount =
			lastNonLtlStopCountRef.current ?? DEFAULT_NON_LTL_SKELETON_STOP_COUNT;
		const placeholders = Array.from({ length: stopCount }, (_, index) =>
			renderNonLtlCardSectionSkeleton(`skeleton-${String(index)}`),
		);

		return (
			<div
				className={styles.cardSectionList}
				data-testid={"shipment-details-loading"}
			>
				{placeholders}
			</div>
		);
	}

	if (isNotFound || isError || !data) {
		return (
			<div
				className={styles.notFoundMessage}
				data-testid={"shipment-details-not-found"}
			>
				No shipment details available for this load.
			</div>
		);
	}

	if (isLtl) {
		const pickup = data.pickups[0] ?? null;
		const delivery = data.deliveries[0] ?? null;

		if (!pickup && !delivery) {
			return (
				<div
					className={styles.notFoundMessage}
					data-testid={"shipment-details-not-found"}
				>
					No shipment details available for this load.
				</div>
			);
		}

		return (
			<div
				className={`${styles.sectionList}${mobileClass}`}
				data-testid={"shipment-details"}
			>
				<LtlCarrierSummaryRow carrier={carrier} isLoading={isCarrierLoading} />
				<LtlStopSection isLoading={false} stop={pickup} type={"pickup"} />
				<LtlStopSection isLoading={false} stop={delivery} type={"delivery"} />
			</div>
		);
	}

	// Non-LTL branch.
	if (data.pickups.length === 0 && data.deliveries.length === 0) {
		return (
			<div
				className={styles.notFoundMessage}
				data-testid={"shipment-details-not-found"}
			>
				No shipment details available for this load.
			</div>
		);
	}

	lastNonLtlStopCountRef.current = data.pickups.length + data.deliveries.length;

	return (
		<NonLtlShipmentDetailsCard
			deliveries={data.deliveries}
			pickups={data.pickups}
		/>
	);
}
